import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import apiService from '../../services/api';
import { BuildConfiguration } from '../../types/api';
import Logger from '../../utils/logger';
import { getApiErrorMessage } from '../../constants/messages';
import { Button, Spinner } from '../ui';
import BuildConfigModal from './BuildConfigModal';
import { useTranslation } from 'react-i18next';

interface BuildConfigListProps {
  repositoryId: number;
  repositoryName: string;
  onBuildTriggered?: () => void;
}

const BuildConfigList: React.FC<BuildConfigListProps> = ({
  repositoryId,
  repositoryName,
  onBuildTriggered
}) => {
  const { t } = (useTranslation as any)('builds');
  const [configs, setConfigs] = useState<BuildConfiguration[]>([]);
  const [loading, setLoading] = useState(true);
  const [triggeringBuild, setTriggeringBuild] = useState<number | null>(null);
  const [deletingConfig, setDeletingConfig] = useState<number | null>(null);
  // UNE LECTURE QUI ECHOUE N'EST PAS UNE LISTE VIDE.
  //
  // Ce composant traduisait toute erreur en « aucune configuration » -- c'etait
  // ecrit en toutes lettres : « Don't show error toast - just show empty state ».
  // Le 2026-10-24, l'API rendait 500 sur trois depots de prod dont les
  // configurations EXISTAIENT (une ligne anterieure a une regle de validation
  // faisait tomber la liste entiere). L'ecran invitait alors a en creer une,
  // c'est-a-dire a poser un DOUBLON a cote de celles qu'on ne voyait pas.
  //
  // Ne pas savoir doit se dire, jamais s'afficher comme un fait.
  const [erreur, setErreur] = useState<string | null>(null);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<BuildConfiguration | null>(null);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      setErreur(null);
      const response = await apiService.listBuildConfigurations(repositoryId);
      setConfigs(response.configs);
    } catch (error) {
      Logger.error('Error fetching build configs:', error);
      setConfigs([]);
      setErreur(getApiErrorMessage(error, t('buildConfig.loadError') as string));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigs();
  }, [repositoryId]);

  const handleTriggerBuild = async (config: BuildConfiguration) => {
    try {
      setTriggeringBuild(config.id);
      const result = await apiService.triggerBuildFromConfig(repositoryId, config.id);
      if (result.success) {
        toast.success(t('buildConfig.triggerSuccess', { name: config.name }) as string);
        onBuildTriggered?.();
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error) || t('buildConfig.triggerError') as string);
    } finally {
      setTriggeringBuild(null);
    }
  };

  const handleDeleteConfig = async (config: BuildConfiguration) => {
    if (!window.confirm(t('buildConfig.deleteConfirm', { name: config.name }) as string)) {
      return;
    }

    try {
      setDeletingConfig(config.id);
      await apiService.deleteBuildConfiguration(repositoryId, config.id);
      toast.success(t('buildConfig.deleteSuccess') as string);
      fetchConfigs();
    } catch (error) {
      toast.error(getApiErrorMessage(error) || t('buildConfig.deleteError') as string);
    } finally {
      setDeletingConfig(null);
    }
  };

  const handleEdit = (config: BuildConfiguration) => {
    setEditingConfig(config);
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    setEditingConfig(null);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingConfig(null);
  };

  const handleConfigured = () => {
    fetchConfigs();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-text-primary">
            {t('buildConfig.title')}
          </h3>
          <p className="text-sm text-text-tertiary">
            {t('buildConfig.subtitle')}
          </p>
        </div>
        <Button variant="primary" onClick={handleCreate}>
          + Nouvelle config
        </Button>
      </div>

      {/* List */}
      {erreur ? (
        <div className="text-center py-8 bg-surface-0 rounded-lg border border-red-500/30">
          <h4 className="text-sm font-medium text-text-primary">
            {t('buildConfig.loadErrorTitle')}
          </h4>
          <p className="mt-1 text-sm text-text-tertiary">{erreur}</p>
          <p className="mt-1 text-xs text-text-quaternary">
            {t('buildConfig.loadErrorHint')}
          </p>
          <Button variant="secondary" className="mt-4" onClick={fetchConfigs}>
            {t('buildConfig.retry')}
          </Button>
        </div>
      ) : configs.length === 0 ? (
        <div className="text-center py-8 bg-surface-0 rounded-lg">
          <svg
            className="mx-auto h-12 w-12 text-text-quaternary"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
            />
          </svg>
          <h4 className="mt-2 text-sm font-medium text-text-primary">
            {t('buildConfig.noConfigTitle')}
          </h4>
          <p className="mt-1 text-sm text-text-tertiary">
            {t('buildConfig.noConfigDesc')}
          </p>
          <Button variant="primary" className="mt-4" onClick={handleCreate}>
            {t('buildConfig.createConfig')}
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {configs.map((config) => (
            <div
              key={config.id}
              className="bg-surface-2 border border-border-1 rounded-lg p-4"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-text-primary">
                      {config.name}
                    </h4>
                    {config.is_default && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-blue-500/12 text-blue-400 rounded">
                        Par défaut
                      </span>
                    )}
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded ${
                        config.auto_build_enabled
                          ? 'bg-green-500/12 text-green-400'
                          : 'bg-surface-3 text-text-tertiary'
                      }`}
                    >
                      {config.auto_build_enabled ? t('buildConfig.active') as string : t('buildConfig.inactive') as string}
                    </span>
                  </div>

                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-text-tertiary">
                    <span className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {config.build_strategy === 'dockerfile' ? 'Dockerfile' : 'Buildpacks'}
                    </span>

                    {config.source_directory !== '.' && (
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                        </svg>
                        {config.source_directory}
                      </span>
                    )}

                    {config.path_filter && (
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                        </svg>
                        {config.path_filter}
                      </span>
                    )}

                    {config.branch_filter && (
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                        {config.branch_filter}
                      </span>
                    )}
                  </div>

                  <div className="mt-2 flex gap-2 text-xs">
                    {config.trigger_on_push && (
                      <span className="px-2 py-0.5 bg-surface-3 text-text-secondary rounded">
                        Push
                      </span>
                    )}
                    {config.trigger_on_tag && (
                      <span className="px-2 py-0.5 bg-surface-3 text-text-secondary rounded">
                        Tags
                      </span>
                    )}
                    {config.trigger_on_pr && (
                      <span className="px-2 py-0.5 bg-surface-3 text-text-secondary rounded">
                        PR
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 ml-4">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleTriggerBuild(config)}
                    loading={triggeringBuild === config.id}
                    disabled={triggeringBuild !== null}
                  >
                    Build
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleEdit(config)}
                  >
                    Modifier
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDeleteConfig(config)}
                    loading={deletingConfig === config.id}
                    disabled={deletingConfig !== null}
                  >
                    Supprimer
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      <BuildConfigModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        repositoryId={repositoryId}
        repositoryName={repositoryName}
        currentConfig={editingConfig}
        onConfigured={handleConfigured}
      />
    </div>
  );
};

export default BuildConfigList;
